import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAllVehicles } from '../../../lib/vehicleService';

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [vehicleCount, setVehicleCount] = useState<number>(100);

  useEffect(() => {
    setIsVisible(true);
    fetchAllVehicles()
      .then((data) => setVehicleCount(data.filter((v) => !v.sold).length))
      .catch(() => setVehicleCount(100));
  }, []);

  return (
    <section className="relative min-h-[75vh] flex items-center justify-center overflow-hidden pt-28 sm:pt-32">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/797fd0533b6d241d4b373460d445c062.jpg"
          alt="Paramount Motors — Every Vehicle, Every Budget"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/85"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 sm:px-8 lg:px-16 py-16 w-full max-w-6xl mx-auto">
        <div
          className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center justify-center gap-3 sm:gap-4 mb-5">
            <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
            <span
              className="text-[#C9A84C] text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Trusted by Buyers Across 48 States
            </span>
            <div className="h-px w-8 sm:w-12 bg-[#C9A84C]"></div>
          </div>

          <h1
            className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white mb-5 leading-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.9)]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Every Vehicle,
            <span className="block text-[#C9A84C]">Every Budget.</span>
          </h1>

          {/* Live Inventory Count Badge */}
          <div className="flex items-center justify-center mb-5">
            <div className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-sm border border-[#C9A84C]/40 px-5 py-2.5 rounded-full">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C9A84C] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C9A84C]"></span>
              </span>
              <span
                className="text-white text-xs sm:text-sm font-semibold tracking-wide"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                <span className="text-[#C9A84C] font-bold">{vehicleCount}</span> Vehicles Available Now
              </span>
              <i className="ri-arrow-right-line text-[#C9A84C] text-xs"></i>
            </div>
          </div>

          <p
            className="text-white text-sm sm:text-base max-w-2xl mx-auto mb-8 leading-relaxed px-4 font-semibold drop-shadow-[0_1px_8px_rgba(0,0,0,1)]"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            From work trucks and family SUVs to everyday sedans and performance vehicles — we carry it all.
            Don't see what you need? We'll source it for you and ship it straight to your door across 48 states.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 mb-12">
            <Link
              to="/inventory"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] px-8 sm:px-10 py-4 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Browse Inventory
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/financing"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-transparent border border-white/40 hover:border-[#C9A84C] text-white hover:text-[#C9A84C] px-8 sm:px-10 py-4 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-105 transform"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Finance Your Vehicle
              <i className="ri-bank-line"></i>
            </Link>
          </div>

          {/* Stats Bar */}
          <div
            className={`flex flex-row items-center justify-center divide-x divide-white/20 transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="text-center px-8 sm:px-14">
              <h3
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-1 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {vehicleCount}+
              </h3>
              <p
                className="text-white/70 text-[10px] sm:text-xs tracking-widest uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Vehicles Available
              </p>
            </div>
            <div className="text-center px-8 sm:px-14">
              <h3
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-1 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                48
              </h3>
              <p
                className="text-white/70 text-[10px] sm:text-xs tracking-widest uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                States We Ship To
              </p>
            </div>
            <div className="text-center px-8 sm:px-14">
              <h3
                className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-1 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                15+
              </h3>
              <p
                className="text-white/70 text-[10px] sm:text-xs tracking-widest uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Years Experience
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden sm:block">
        <div className="w-6 h-10 border border-white/30 rounded-full flex items-start justify-center p-2 cursor-pointer hover:border-[#C9A84C] transition-colors">
          <div className="w-1 h-2 bg-[#C9A84C] rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}
